/**
 * Emit hosts for the `general` analytics group, callable without a Rematch
 * `dispatch`. Functions lifted from `analyticsScribeModel`'s effects keep the
 * effect's own payload.
 */
import { USE_RECORDING_V2 } from '@/components/constants/flags';
import { getDemoModeForSession } from '@/features/demo-flow/utils/getDemoModeForSession';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type NoteAutoRetryDecidedEventProps,
  type NoteFailureOverlayClearedEventProps,
  type NoteFailurePresentedEventProps,
} from '@/models/analytics/features/notes';
import {
  SegmentActionName,
  SegmentObjectName,
  type ShortcutContext,
} from '@/models/analytics/segment-names';
import { logger } from '@/utils/logger/logger';
import { type GenerationMethod } from '@heidi/schema/ml-scribe';
import { type NoteFailedErrorCode } from '@heidi/session/contracts';
import { type UpdateUserRequest } from '@heidi/workspace/contracts';

export function gotFirstNonEmptyTranscriptChunk({
  sessionId,
  isTabSharing,
}: {
  sessionId: string;
  isTabSharing?: boolean;
}) {
  logger.info({
    event: SegmentActionName.GotFirstNonEmptyTranscriptChunk,
    sessionId,
    isRecordingV2Enabled: USE_RECORDING_V2(),
    isTabSharing: isTabSharing,
  });

  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.GotFirstNonEmptyTranscriptChunk,
      sessionId,
      isRecordingV2Enabled: USE_RECORDING_V2(),
      isTabSharing: isTabSharing,
    },
  });
}

export function integrationReadyModalClosed(input: { stay?: boolean }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: input.stay
        ? SegmentActionName.IntegrationReadyModalClosedAndStay
        : SegmentActionName.IntegrationReadyModalClosedAndBack,
    },
  });
}

export function markSessionAsContextOnly(sessionId?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.SessionMarkedAsContextOnly,
    },
  });
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionMarkedAsContextOnly,
    props: { session_id: sessionId },
  });
}

export function navigationDiscoveryCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationDiscoveryCall,
    },
  });
}

export function navigationFranceCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationFranceCall,
    },
  });
}

export function navigationGermanCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationGermanCall,
    },
  });
}

export function navigationSpanishCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationSpanishCall,
    },
  });
}

export function noteGenerationEnd({
  sessionId,
  documentId,
  endTime,
  is_pinned_template,
  is_starter_pack_template,
}: {
  sessionId: string;
  documentId?: string;
  endTime: number;
  is_pinned_template: boolean;
  is_starter_pack_template: boolean;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NoteGenerationEnd,
      sessionId,
      documentId,
      noteGenerationEndTime: endTime,
      is_pinned_template,
      is_starter_pack_template,
    },
  });
}

export function noteAutoRetryDecided(props: NoteAutoRetryDecidedEventProps) {
  track({
    event_name: TrackingEventName.NoteAutoRetryDecided,
    props,
    options: { amplitude: false },
  });
}

export function noteFailurePresented(props: NoteFailurePresentedEventProps) {
  track({
    event_name: TrackingEventName.NoteFailurePresented,
    props,
    options: { amplitude: false },
  });
}

export function noteFailureOverlayCleared(props: NoteFailureOverlayClearedEventProps) {
  track({
    event_name: TrackingEventName.NoteFailureOverlayCleared,
    props,
    options: { amplitude: false },
  });
}

export function noteGenerationError({
  sessionId,
  error_code,
  documentId,
  is_pinned_template,
  is_starter_pack_template,
  generation_method,
}: {
  sessionId: string;
  error_code: NoteFailedErrorCode;
  documentId?: string;
  is_pinned_template?: boolean;
  is_starter_pack_template?: boolean;
  generation_method?: GenerationMethod | null;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NoteGenerationError,
      sessionId,
      documentId,
    },
  });

  // V2: Amplitude + Datadog. Snowplow handler is still unwired
  // (`TODO(data)` in lib/snowplow/events/notes.ts). `error_code` is the
  // group-by; do not send free-text `error_message` (PHI).
  track({
    event_name: TrackingEventName.NoteFailed,
    props: {
      session_id: sessionId,
      document_id: documentId,
      error_code,
      is_pinned_template,
      is_starter_pack_template,
      generation_method,
    },
  });
}

// settings
export function noteGenerationFinished({
  isSuccess,
  sessionId,
  documentId,
  totalDuration,
  durationBeforeWaitingUpload,
  durationWaitingToUpload,
  durationWaitingToStream,
  durationStreaming,
  durationCompletingStreaming,
  isRecordingV2Enabled,
  isTabSharing,
  error,
}: {
  isSuccess: boolean;
  sessionId: string;
  documentId?: string;
  totalDuration?: number;
  durationBeforeWaitingUpload?: number;
  durationWaitingToUpload?: number;
  durationWaitingToStream?: number;
  durationStreaming?: number;
  durationCompletingStreaming?: number;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  error?: string;
}) {
  logger.info('Note generation finished', {
    event: 'note_generation_finished',
    isSuccess,
    sessionId,
    documentId,
    totalDuration,
    durationBeforeWaitingUpload,
    durationWaitingToUpload,
    durationWaitingToStream,
    durationStreaming,
    durationCompletingStreaming,
    error,
    isRecordingV2Enabled: USE_RECORDING_V2(),
    isTabSharing: isTabSharing,
  });
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NoteGenerationEnd,
      sessionId,
      isSuccess,
      documentId,
      totalDuration,
      durationBeforeWaitingUpload,
      durationWaitingToUpload,
      durationWaitingToStream,
      durationStreaming,
      durationCompletingStreaming,
      isRecordingV2Enabled: isRecordingV2Enabled,
      isTabSharing: isTabSharing,
    },
  });
}

export function noteGenerationStart({
  sessionId,
  documentId,
  startTime,
  isRecordingV2Enabled,
  isTabSharing,
  is_pinned_template,
  is_starter_pack_template,
  generation_method,
}: {
  sessionId: string;
  documentId?: string;
  startTime: number;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  is_pinned_template: boolean;
  is_starter_pack_template: boolean;
  generation_method?: GenerationMethod | null;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NoteGenerationStart,
      sessionId,
      documentId,
      noteGenerationStartTime: startTime,
      isRecordingV2Enabled: isRecordingV2Enabled,
      isTabSharing: isTabSharing,
      is_pinned_template,
      is_starter_pack_template,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteStarted,
    props: {
      session_id: sessionId,
      document_id: documentId,
      start_time_ms: startTime,
      is_recording_v2_enabled: isRecordingV2Enabled,
      is_tab_sharing: isTabSharing,
      is_pinned_template,
      is_starter_pack_template,
      generation_method,
      // tag note_started when generation runs for the demo
      // session. Fires for both the natural-end and skip-to-results paths.
      demo_mode: getDemoModeForSession(sessionId),
    },
  });
}

export function saveSettings(settingsUpdatedData: UpdateUserRequest) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.SaveSettings,
      settingsUpdatedData,
    },
  });
}

export function tauriSessionMismatch() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_SESSION_MISMATCH,
    },
  });
}

export function trackBackupSizeZeroError(input: { sessionId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.BackupSizeZeroError,
      sessionId: input.sessionId,
    },
  });
}

export function trackChunkSessionMismatch(input: {
  currentSessionId: string;
  newSessionId: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.SessionChunkMismatchError,
      currentSessionId: input.currentSessionId,
      newSessionId: input.newSessionId,
    },
  });
}

export function trackDefaultDeviceErrorTauri() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_DEFAULT_DEVICE_ERROR,
    },
  });
}

export function trackDeleteAccountSuccess(input: {
  userId?: string;
  reason?: string;
  details?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.DeleteAccountSuccess,
      userId: input.userId,
      reason: input.reason,
      details: input.details,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AccountDeleted,
    props: {
      reason: input.reason,
      details: input.details,
    },
  });
}

export function trackFailedToSendChunk(input: {
  sessionId: string;
  recordingId: string;
  chunkId: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedToSendChunk,
      sessionId: input.sessionId,
      recordingId: input.recordingId,
      chunkId: input.chunkId,
    },
  });
}

export function trackFailedToStartChunkRecording(error?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedToStartChunkRecording,
      error: error || '',
    },
  });
}

export function trackGetCurrentRecordingFailed({
  speakerSize,
  audioSize,
}: {
  speakerSize?: number;
  audioSize?: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_GET_RECORDING_FAILED,
      speakerSize: speakerSize,
      audioSize: audioSize,
    },
  });
}

export function trackMediaRecorderFailedToStart(input: {
  sessionId: string;
  recordingId: string;
  chunkCount: number;
  error: string;
}) {
  const { sessionId, recordingId, chunkCount, error } = input;
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedToStartMediaRecorderChunk,
      sessionId: sessionId,
      recordingId: recordingId,
      chunkCount: chunkCount,
      error: error,
    },
  });
}

export function trackMediaRecorderUnableToStop() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.MediaRecorderUnableToStop,
    },
  });
}

export function trackMediaStreamChunkTrackStateError(input: { error: string }) {
  const { error } = input;
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedTrackStatusInChunk,
      error: error,
    },
  });
}

export function trackRecordingTriesToStartWhenStatusIsRecording() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.RecordingTriesToStartWhenStatusIsRecording,
    },
  });
}

export function trackShortcutUsed({ shortcutName }: { shortcutName: ShortcutContext }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.ShortcutUsed,
      shortcutName: shortcutName,
    },
  });
}

export function trackSnippetCreated(input: {
  contentLength: number;
  shorthandLength: number;
  from: 'editor' | 'settings' | 'dictation';
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.CreateSnippet,
      contentLength: input.contentLength,
      shorthandLength: input.shorthandLength,
      from: input.from,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SnippetCreated,
    props: {
      content_length: input.contentLength,
      shorthand_length: input.shorthandLength,
      source: input.from,
    },
  });
}

export function trackSnippetDeleted(input: {
  snippetId: string;
  from: 'editor' | 'settings' | 'dictation';
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.DeleteSnippet,
      snippetId: input.snippetId,
      from: input.from,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SnippetDeleted,
    props: {
      snippet_id: input.snippetId,
      source: input.from,
    },
  });
}

export function trackSnippetEdited(input: {
  snippetId: string;
  contentLength: number;
  shorthandLength: number;
  from: 'editor' | 'settings' | 'dictation';
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.EditSnippet,
      snippetId: input.snippetId,
      contentLength: input.contentLength,
      shorthandLength: input.shorthandLength,
      from: input.from,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SnippetUpdated,
    props: {
      snippet_id: input.snippetId,
      content_length: input.contentLength,
      shorthand_length: input.shorthandLength,
      source: input.from,
    },
  });
}

export function trackSnippetOpenPrompt() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.OpenSnippetPrompt,
    },
  });
}

export function trackSnippetUsed(input: {
  snippetId: string;
  contentLength: number;
  shorthandLength: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.UseSnippet,
      snippetId: input.snippetId,
      contentLength: input.contentLength,
      shorthandLength: input.shorthandLength,
    },
  });
}

export function trackToggleUserMfa(input: { enabled: boolean }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.UserMfaToggle,
      enabled: input.enabled,
    },
  });
}

export function trackUseHeidiButtonClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.UseHeidiClicked,
    },
  });
}
